import { useState, useEffect } from 'react';
import { collection, query, where, getDocs, Timestamp, doc, getDoc, setDoc, deleteDoc } from 'firebase/firestore';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Building, UserX, Plus } from 'lucide-react';
import { removeMemberAndCleanupHabits } from '@/lib/firebase/queries/organizations/members';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import { db } from '@/lib/firebase/config';
import type { User } from '@/types';
import type { Organization } from '@/types/organization';

interface UserOrganizationsProps {
  user: User;
  onUpdate?: () => void;
}

interface UserOrganization {
  id: string;
  organization: Organization;
  role: string;
  joinedAt: Date;
}

export function UserOrganizations({ user, onUpdate }: UserOrganizationsProps) {
  const [organizations, setOrganizations] = useState<UserOrganization[]>([]);
  const [availableOrganizations, setAvailableOrganizations] = useState<Organization[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingOrgs, setIsLoadingOrgs] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState<string>('');
  // Role is fixed to 'customer' in this context
  const [isAdding, setIsAdding] = useState(false);
  const { t, dir } = useLanguage();
  const isRTL = dir === 'rtl';

  useEffect(() => {
    loadUserOrganizations();
  }, [user.id]);

  const loadAvailableOrganizations = async () => {
    try {
      setIsLoadingOrgs(true);
      
      // Get all organizations
      const orgsQuery = query(collection(db, 'organizations'));
      const orgsSnapshot = await getDocs(orgsQuery);
      
      const orgs: Organization[] = [];
      orgsSnapshot.forEach(doc => {
        orgs.push({ id: doc.id, ...doc.data() } as Organization);
      });
      
      // Filter out organizations the user is already a member of
      const userOrgIds = organizations.map(org => org.organization.id);
      const availableOrgs = orgs.filter(org => !userOrgIds.includes(org.id));
      
      setAvailableOrganizations(availableOrgs);
    } catch (error) {
      console.error('Error loading available organizations:', error);
      toast.error(t('dashboard.admin.users.organizations.loadOrgsError'));
    } finally {
      setIsLoadingOrgs(false);
    }
  };

  const handleAddToOrganization = async () => {
    if (!selectedOrg) {
      toast.error(t('dashboard.admin.users.organizations.selectOrgError'));
      return;
    }

    try {
      setIsAdding(true);
      
      // Add user to organization with deterministic ID to prevent duplicates
      const memberId = `${selectedOrg}_${user.id}_customer`;
      const memberRef = doc(collection(db, 'organizationMembers'), memberId);
      const existing = await getDoc(memberRef);
      if (existing.exists()) {
        await setDoc(
          memberRef,
          { role: 'customer', updatedAt: Timestamp.now() },
          { merge: true }
        );
      } else {
        await setDoc(memberRef, {
          userId: user.id,
          organizationId: selectedOrg,
          role: 'customer',
          joinedAt: Timestamp.now(),
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
        });
      }

      // Note: Organization default habits system has been removed
      toast.success(t('dashboard.admin.users.organizations.addSuccess'));

      setShowAddDialog(false);
      setSelectedOrg('');
      // role is fixed; nothing to reset
      await loadUserOrganizations(); // Reload the list
      onUpdate?.();
    } catch (error) {
      console.error('Error adding user to organization:', error);
      toast.error(t('dashboard.admin.users.organizations.addError'));
    } finally {
      setIsAdding(false);
    }
  };

  const loadUserOrganizations = async () => {
    try {
      setIsLoading(true);
      
      // Get user's organization memberships (only customer role)
      const membershipsQuery = query(
        collection(db, 'organizationMembers'),
        where('userId', '==', user.id),
        where('role', '==', 'customer')
      );
      const membershipsSnapshot = await getDocs(membershipsQuery);
      
      const userOrgs: UserOrganization[] = [];
      
      for (const membershipDoc of membershipsSnapshot.docs) {
        const membershipData = membershipDoc.data();
        
        // Get organization details
        const orgQuery = query(
          collection(db, 'organizations'),
          where('id', '==', membershipData.organizationId)
        );
        const orgSnapshot = await getDocs(orgQuery);
        
        if (!orgSnapshot.empty) {
          const orgData = orgSnapshot.docs[0].data() as Organization;
          userOrgs.push({
            id: membershipDoc.id,
            organization: orgData,
            role: membershipData.role,
            joinedAt: membershipData.joinedAt?.toDate() || new Date(),
          });
        }
      }
      
      setOrganizations(userOrgs);
    } catch (error) {
      console.error('Error loading user organizations:', error);
      toast.error(t('dashboard.admin.users.organizations.loadError'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveFromOrganization = async (membershipId: string, orgName: string) => {
    if (!confirm(t('dashboard.admin.users.organizations.removeConfirm', { org: orgName }))) {
      return;
    }

    try {
      // Remove membership and cleanup copied default habits
      const membership = organizations.find(o => o.id === membershipId);
      if (membership) {
        await removeMemberAndCleanupHabits(membership.organization.id, user.id, membership.role as any);
      } else {
        // fallback: delete doc directly
        await deleteDoc(doc(db, 'organizationMembers', membershipId));
      }
      toast.success(t('dashboard.admin.users.organizations.removeSuccess', { org: orgName }));
      await loadUserOrganizations(); // Reload the list
      onUpdate?.();
    } catch (error) {
      console.error('Error removing user from organization:', error);
      toast.error(t('dashboard.admin.users.organizations.removeError'));
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4" dir={dir}>
          <div className={`flex ${isRTL ? 'flex-row' : 'flex-row'} justify-between items-center`}>
        <div className={isRTL ? 'text-right' : 'text-left'}>
          <h3 className="text-lg font-semibold">
                {isRTL ? 'عرض وإدارة عضوية المستخدم في المنظمات كمستفيد' : t('dashboard.admin.users.organizations.title')}
          </h3>
          <p className="text-sm text-muted-foreground">
            {t('dashboard.admin.users.organizations.description')}
          </p>
        </div>
        <div className={`flex ${isRTL ? 'flex-row-reverse' : 'flex-row'} gap-2`}>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              loadAvailableOrganizations();
              setShowAddDialog(true);
            }}
          >
            {isRTL ? (
              <>
                {t('dashboard.admin.users.organizations.addToOrg')}
                <Plus className="h-4 w-4 ml-2" />
              </>
            ) : (
              <>
                <Plus className="h-4 w-4 mr-2" />
                {t('dashboard.admin.users.organizations.addToOrg')}
              </>
            )}
          </Button>
        </div>
      </div>

      {organizations.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-6">
            <Building className="h-10 w-10 text-muted-foreground mb-3" />
            <p className="text-muted-foreground text-center">
              {t('dashboard.admin.users.organizations.noOrganizations')}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {organizations.map((userOrg) => (
            <Card key={userOrg.id} className="p-4">
              <div className={`flex ${isRTL ? 'flex-row' : 'flex-row'} justify-between items-center gap-4`}>
                <div className="flex-1">
                    <div className={`flex ${isRTL ? 'flex-row' : 'flex-row'} items-center gap-3`}>
                    <div className={isRTL ? 'text-right' : 'text-left'}>
                      <h4 className="font-semibold text-base">
                        {isRTL 
                          ? (userOrg.organization.nameAr || userOrg.organization.name || userOrg.organization.nameEn)
                          : (userOrg.organization.nameEn || userOrg.organization.name || userOrg.organization.nameAr)
                        }
                      </h4>
                      {!isRTL && (
                        <p className="text-sm text-muted-foreground">
                          {userOrg.organization.nameAr || userOrg.organization.name}
                        </p>
                      )}
                    </div>
                    <div className={`flex ${isRTL ? 'flex-row-reverse' : 'flex-row'} items-center gap-2`}>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        userOrg.role === 'admin' ? 'bg-red-100 text-red-800' :
                        userOrg.role === 'manager' ? 'bg-blue-100 text-blue-800' :
                        userOrg.role === 'examiner' ? 'bg-purple-100 text-purple-800' :
                        userOrg.role === 'coach' ? 'bg-green-100 text-green-800' :
                        userOrg.role === 'instructor' ? 'bg-yellow-100 text-yellow-800' :
                        userOrg.role === 'partner' ? 'bg-orange-100 text-orange-800' :
                        userOrg.role === 'volunteer' ? 'bg-pink-100 text-pink-800' :
                        userOrg.role === 'organizer' ? 'bg-indigo-100 text-indigo-800' :
                        userOrg.role === 'provider' ? 'bg-teal-100 text-teal-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {t(`dashboard.admin.users.roles.${userOrg.role}`) || userOrg.role}
                      </span>
                    </div>
                  </div>
                  <div className={`flex ${isRTL ? 'flex-row' : 'flex-row'} gap-4 mt-2 text-xs text-muted-foreground`}>
                    <span>
                      {t('dashboard.admin.users.organizations.joined')}: {userOrg.joinedAt.toLocaleDateString()}
                    </span>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleRemoveFromOrganization(
                    userOrg.id,
                    isRTL
                      ? (userOrg.organization.nameAr || userOrg.organization.name || userOrg.organization.nameEn)
                      : (userOrg.organization.nameEn || userOrg.organization.name || userOrg.organization.nameAr)
                  )}
                  className="flex-shrink-0"
                >
                  {isRTL ? (
                    <>
                      {t('dashboard.admin.users.organizations.remove')}
                      <UserX className="h-4 w-4 ml-2" />
                    </>
                  ) : (
                    <>
                      <UserX className="h-4 w-4 mr-2" />
                      {t('dashboard.admin.users.organizations.remove')}
                    </>
                  )}
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Add to Organization Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-md" dir={dir}>
          <DialogHeader className={isRTL ? 'text-right' : 'text-left'}>
            <DialogTitle>{t('dashboard.admin.users.organizations.addToOrgTitle')}</DialogTitle>
            <DialogDescription>
              {t('dashboard.admin.users.organizations.addToOrgDesc')}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <label className={`text-sm font-medium ${isRTL ? 'text-right' : 'text-left'}`}>
                {t('dashboard.admin.users.organizations.selectOrganization')}
              </label>
              <Select value={selectedOrg} onValueChange={setSelectedOrg}>
                <SelectTrigger className={isRTL ? 'text-right' : 'text-left'}>
                  <SelectValue placeholder={t('dashboard.admin.users.organizations.selectOrgPlaceholder')} />
                </SelectTrigger>
                <SelectContent>
                  {isLoadingOrgs ? (
                    <div className="flex items-center justify-center p-4">
                      <Loader2 className="h-4 w-4 animate-spin" />
                    </div>
                  ) : availableOrganizations.length === 0 ? (
                    <div className="p-4 text-center text-muted-foreground">
                      {t('dashboard.admin.users.organizations.noAvailableOrgs')}
                    </div>
                  ) : (
                    availableOrganizations.map((org) => (
                       <SelectItem key={org.id} value={org.id}>
                         {isRTL 
                           ? (org.nameAr || org.name || org.nameEn)
                           : (org.nameEn || org.name || org.nameAr)
                         }
                       </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Role selection removed; user is added as customer by default */}

            <div className={`flex ${isRTL ? 'justify-start' : 'justify-end'} gap-2 pt-4`}>
              <Button
                variant="outline"
                onClick={() => setShowAddDialog(false)}
              >
                {t('common.cancel')}
              </Button>
              <Button
                onClick={handleAddToOrganization}
                disabled={isAdding || !selectedOrg}
              >
                {isAdding ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    {t('dashboard.admin.users.organizations.adding')}
                  </>
                ) : (
                  t('dashboard.admin.users.organizations.add')
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
} 